import { useCallback, useEffect, useState } from "react";

import { useUserData } from "Flocknote/apiHooks";

import { useSubmitting } from "../paymentMethodStateHooks";

import useCreditCardEntryState from "./state";
import { getErrorMessageType } from "./staxTokenizationErrors";
import { useCCValidations } from "./useCCValidations";
import useStaxAPI from "./useStaxAPI";

export default function Stax({
  children,
  getPaymentMethodTokenTrigger = false,
  setPaymentMethodTokenInfo,
  setInputErrors,
}) {
  useStaxAPI();

  const { submitting, setSubmitting } = useSubmitting();
  const [userData] = useUserData();

  const [validationIsReady, setValidationIsReady] = useState(false);

  const [paymentMethodData, dispatch] = useCreditCardEntryState();
  const {
    ccNumber,
    cvc,
    month,
    year,
    name,
    zip,
    cardType,
    displayErrorMessages,
    ccInputsValidated,
    canBeEdited,
    shouldRetokenize,
    paymentMethodID,
    last4,
  } = paymentMethodData;

  const cardTypeName = cardType?.shortname || "";

  const shouldUpdateCardWithoutRetokenizing = canBeEdited && !shouldRetokenize;

  const resetState = useCallback(() => {
    dispatch({
      type: "Set Editing",
      payload: false,
    });
    dispatch({
      type: "Should Be Retokenized",
      payload: false,
    });
  }, [dispatch]);

  const savePaymentMethodTokenInfo = useCallback(
    (res) => {
      setPaymentMethodTokenInfo({
        cardType: res.card_type,
        paymentMethodToken: res.id,
        name: name.trim(),
        month,
        year,
        zip,
        last4: res.card_last_four,
        replaceExistingCard: shouldRetokenize,
        paymentMethodID,
      });
    },
    [
      month,
      name,
      paymentMethodID,
      setPaymentMethodTokenInfo,
      shouldRetokenize,
      year,
      zip,
    ]
  );

  const tokenizePaymentMethod = useCallback(async () => {
    const { firstName, lastName } = getFirstAndLastNameFromName_naive(name);

    let details = {
      firstname: firstName,
      lastname: lastName,
      method: "card",
      month: (month * 1).toString(),
      year: `20${year}`,
      phone: userData?.phoneInfo?.phone,
      email: userData?.emailInfo?.email,
      address_1: userData?.addressInfo?.address1 || undefined,
      address_2: userData?.addressInfo?.address2 || undefined,
      address_city: userData?.addressInfo?.city || undefined,
      address_state: userData?.addressInfo?.state || "TX",
      address_zip: zip,
    };
    // console.log({ details });

    window.staxjs
      .tokenize(details)
      .then((res) => {
        savePaymentMethodTokenInfo(res);
        resetState();
      })
      .catch((err) => {
        // console.log({ err });
        const errorMessageType = getErrorMessageType(err);
        setInputErrors([errorMessageType]);
        setSubmitting(false);
      });
  }, [
    month,
    resetState,
    savePaymentMethodTokenInfo,
    year,
    setSubmitting,
    setInputErrors,
    name,
  ]);

  const triggerUpdateWithoutTokenizing = useCallback(() => {
    setPaymentMethodTokenInfo({
      cardType: cardTypeName,
      paymentMethodToken: null,
      name: name.trim(),
      month,
      year,
      zip,
      last4,
      paymentMethodID,
    });
    resetState();
  }, [
    cardTypeName,
    last4,
    month,
    name,
    paymentMethodID,
    resetState,
    year,
    zip,
    setPaymentMethodTokenInfo,
  ]);

  useEffect(() => {
    if (getPaymentMethodTokenTrigger) setValidationIsReady(true);
  }, [getPaymentMethodTokenTrigger]);

  const clearValidationFlags = () => {
    setValidationIsReady(false);
    dispatch("Clear Display Errors");
  };

  const exclusions = shouldUpdateCardWithoutRetokenizing
    ? ["card_type", "card_number", "expiration_date", "cvc"]
    : ["card_number", "cvc"];

  useCCValidations(
    {
      ccNumber,
      cvc,
      month,
      year,
      name,
      zip,
      cardType,
    },
    validationIsReady,
    () => {
      clearValidationFlags();
      setInputErrors([]);
      dispatch({
        type: "Toggle CC Inputs Validated",
        payload: true,
      });
    },
    (errors) => {
      clearValidationFlags();
      setInputErrors(errors);
      setPaymentMethodTokenInfo({ error: true });
    },
    exclusions
  );

  useEffect(() => {
    if (ccInputsValidated) {
      if (displayErrorMessages.length > 0)
        return setPaymentMethodTokenInfo({ error: true });

      if (shouldUpdateCardWithoutRetokenizing) triggerUpdateWithoutTokenizing();
      else tokenizePaymentMethod();

      dispatch({
        type: "Toggle CC Inputs Validated",
        payload: false,
      });
    }
  }, [
    displayErrorMessages.length,
    setPaymentMethodTokenInfo,
    ccInputsValidated,
    dispatch,
    shouldUpdateCardWithoutRetokenizing,
    triggerUpdateWithoutTokenizing,
    tokenizePaymentMethod,
  ]);

  return children;
}

const getFirstAndLastNameFromName_naive = (name) => {
  const arr = name.trim().split(" ");
  const firstName = arr[0];
  const lastName = arr.slice(1).join(" ");

  return { firstName, lastName };
};
